import { Checkbox, Group, Stack, Text, Textarea } from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import Highlight from "@tiptap/extension-highlight";
import SubScript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { generateJSON, generateText, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState } from "react";

interface ToggleEditorProps {
  label: string;
  placeholder: string;
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: React.ReactNode;
  minRows?: number;
}

export const ToggleEditor = ({
  label,
  placeholder,
  value = "",
  onChange,
  required = false,
  error,
  minRows = 3,
}: ToggleEditorProps) => {
  const [useFancyEditor, setUseFancyEditor] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: "",
    onUpdate: ({ editor }) => {
      // Save as JSON
      const jsonContent = editor.getJSON();
      const content = JSON.stringify(jsonContent);
      onChange(content);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: This must be run only once
  useEffect(() => {
    if (!value) {
      return;
    }
    let isRichContent = false;
    let contentForEditor = value;

    if (value.startsWith("{")) {
      try {
        contentForEditor = JSON.parse(value);
        isRichContent = true;
      } catch {
        contentForEditor = value;
      }
    } else {
      contentForEditor = value;
    }

    if (isRichContent) {
      editor.commands.setContent(contentForEditor);
    }

    setUseFancyEditor(isRichContent);
  }, []);

  const convertPlainTextToJson = (text: string) => {
    const jsonContent = generateJSON(text, [StarterKit]);
    return JSON.stringify(jsonContent);
  };

  const convertJsonToPlainText = (jsonString: string) => {
    try {
      const jsonContent = JSON.parse(jsonString);
      return generateText(jsonContent, [StarterKit]);
    } catch {
      // If it's not JSON, return as is
      return jsonString;
    }
  };

  const handleEditorToggle = (useFancyEditor: boolean) => {
    setUseFancyEditor(useFancyEditor);
    const currentContent = value || "";

    if (useFancyEditor) {
      // Converting to fancy editor - convert plain text to JSON
      const jsonContent = convertPlainTextToJson(currentContent);
      const parsedJson = JSON.parse(jsonContent);
      onChange(jsonContent);
      if (editor) {
        editor.commands.setContent(parsedJson);
      }
    } else {
      // Converting to simple editor - convert JSON to plain text
      const plainContent = convertJsonToPlainText(currentContent);
      onChange(plainContent);
    }
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Text size="sm" fw={500}>
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </Text>
        <Checkbox
          label="Use fancy editor"
          checked={useFancyEditor}
          onChange={(event) => handleEditorToggle(event.currentTarget.checked)}
          size="xs"
        />
      </Group>
      {useFancyEditor ? (
        <RichTextEditor editor={editor}>
          <RichTextEditor.Toolbar sticky stickyOffset={60}>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.Highlight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
              <RichTextEditor.H4 />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Blockquote />
              <RichTextEditor.Hr />
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignJustify />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Undo />
              <RichTextEditor.Redo />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>

          <RichTextEditor.Content />
        </RichTextEditor>
      ) : (
        <Textarea
          placeholder={placeholder}
          minRows={minRows}
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      )}
      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};
